import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Checkbox,
  Icon,
  Skeleton,
  Text,
} from '@mntn/ui-components'
import type React from 'react'
import { useMemo } from 'react'
import { useGenres } from '../../../../hooks/api/use-genres'
import { useFilter } from '../context/filter-context'

const SKELETON_ITEMS = [
  'genre-skeleton-1',
  'genre-skeleton-2',
  'genre-skeleton-3',
  'genre-skeleton-4',
  'genre-skeleton-5',
  'genre-skeleton-6',
]

const GenreFilterSkeleton = () => (
  <div className="space-y-3">
    {SKELETON_ITEMS.map(key => (
      <div key={key} className="flex items-center space-x-3">
        <Skeleton className="w-4 h-4" />
        <Skeleton className="h-4" />
      </div>
    ))}
  </div>
)

export const GenreFilter: React.FC = () => {
  const { filters, updateFilter, isSectionOpen, toggleSection } = useFilter()
  const { genres, isLoading } = useGenres()
  const isOpen = isSectionOpen('genres')

  const selectedGenres = useMemo(() => filters.genreIds || [], [filters.genreIds])

  const handleGenreChange = (genreId: string, checked: boolean) => {
    const updatedGenres = checked ? [...selectedGenres, genreId] : selectedGenres.filter(id => id !== genreId)

    updateFilter('genreIds', updatedGenres.length > 0 ? updatedGenres : undefined)
  }

  const selectedCount = selectedGenres.length
  const allSelected = genres.length > 0 && selectedCount === genres.length

  const handleSelectAll = () => {
    if (allSelected) {
      updateFilter('genreIds', undefined)
    } else {
      updateFilter(
        'genreIds',
        genres.map(genre => genre.id),
      )
    }
  }

  const renderContent = () => {
    if (isLoading) {
      return <GenreFilterSkeleton />
    }

    if (genres.length === 0) {
      return (
        <Text variant="base-sm" className="text-primary p-4">
          No genres available
        </Text>
      )
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Checkbox id="select-all-genres" checked={allSelected} onCheckedChange={handleSelectAll} />
          <Text variant="sm-sb" className="text-header">
            Select All ({genres.length})
          </Text>
        </div>

        <div className="space-y-3">
          {genres.map(genre => (
            <div key={genre.id} className="flex items-center space-x-3">
              <Checkbox
                id={`genre-${genre.id}`}
                checked={selectedGenres.includes(genre.id)}
                onCheckedChange={checked =>
                  // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
                  handleGenreChange(genre.id, checked as boolean)
                }
              />
              <Text variant="base-sm" className="text-header">
                {genre.name}
              </Text>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={isOpen ? 'genres' : ''}
      onValueChange={() => toggleSection('genres')}
      className="w-full"
    >
      <AccordionItem value="genres" className="border-0">
        <AccordionTrigger className="p-6 border-b border-borders-primary hover:no-underline">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Text variant="lg-sb">Genres</Text>
              {selectedCount > 0 && (
                <div className="bg-primary text-header-inverse rounded-full px-2 py-1 text-xs font-medium">
                  {selectedCount}
                </div>
              )}
            </div>
            <Icon
              name={isOpen ? 'Minus' : 'Plus'}
              className="h-6 w-6 text-primary shrink-0 transition-transform duration-200"
            />
          </div>
        </AccordionTrigger>
        <AccordionContent className="p-6 border-b border-borders-primary">{renderContent()}</AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
